import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getUser, getUserGroups, getUserRoleBindings } from '../services/iam';
import { ChartColumn, ChevronRight, FileTerminal, History, KeyRound, UsersRound } from 'lucide-react';
import { accessLogsApi } from '../services/api';
import { usePermissions } from '../hooks/usePermissions';
import { Breadcrumbs, InitialsAvatar, ListPageSkeleton, OutcomeBadge, PageHeader, Skeleton, Timestamp } from '@/components/ds-zta';
import { DetailLoadError, SectionLoadError } from '../components/DetailLoadError';
import { isNotFoundError, retryUnlessClientError } from '@/lib/apiError';
import { userStatusValue } from '@/lib/userStatus';

const usersCrumbs = (current: React.ReactNode) => (
  <Breadcrumbs
    items={[{ label: 'Identity' }, { label: 'Users', href: '/users' }, { label: current }]}
    renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
  />
);

const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const userQuery = useQuery(['user', id], () => getUser(id!), { retry: retryUnlessClientError });
  const groupsQuery = useQuery(['userGroups', id], () => getUserGroups(id!), { retry: retryUnlessClientError });
  const rolesQuery = useQuery(['userRoleBindings', id], () => getUserRoleBindings(id!), { retry: retryUnlessClientError });
  const { data: userData, isLoading: isLoadingUser } = userQuery;
  const { data: groupsData, isLoading: isLoadingGroups } = groupsQuery;
  const { data: rolesData, isLoading: isLoadingRoles } = rolesQuery;

  const isLoading = isLoadingUser || isLoadingGroups || isLoadingRoles;

  // The user's own trail (design eval F22): their latest access decisions,
  // and links into each audit page filtered to them. Each appears only where
  // the viewer may open that page.
  const { canAccessRoute } = usePermissions();
  const canSeeAccessLogs = canAccessRoute('/access-logs');
  const recentLogsQuery = useQuery(
    ['user-access-logs', id],
    () => accessLogsApi.list(id!, undefined, undefined, 5, 0),
    { enabled: !!id && canSeeAccessLogs, retry: retryUnlessClientError },
  );

  if (isLoading || !userData) {
    return (
      <div className="space-y-6">
        <PageHeader
          breadcrumbs={usersCrumbs(isLoading ? 'Loading' : !userQuery.error || isNotFoundError(userQuery.error) ? 'Not found' : 'Error')}
          icon={<UsersRound />}
          title={
            isLoading ? (
              <>
                <span className="sr-only">Loading user details</span>
                <Skeleton variant="text" width={220} />
              </>
            ) : (
              'User'
            )
          }
        />
        {isLoading ? (
          <ListPageSkeleton filterCount={0} rowCount={6} />
        ) : (
          <DetailLoadError
            entity="user"
            error={userQuery.error}
            icon={<UsersRound />}
            onRetry={() => userQuery.refetch()}
            backLabel="Back to users"
            onBack={() => navigate('/users')}
          />
        )}
      </div>
    );
  }

  const user = userData;
  const groups = groupsData?.data || [];
  const roleBindings = rolesData?.data || [];
  const recentLogs = recentLogsQuery.data?.data?.access_logs ?? [];

  // Each page's own URL filter: Access logs and SSH activity read ?user_id=,
  // Sessions ?user=; Access requests has no user filter, only a search over
  // the requester's name, so it gets ?q=<name>.
  const uid = encodeURIComponent(user.id);
  const activityLinks = [
    { route: '/access-logs', href: `/access-logs?user_id=${uid}`, label: 'Access logs', icon: ChartColumn },
    { route: '/sessions', href: `/sessions?user=${uid}`, label: 'Sessions', icon: History },
    { route: '/ssh-activity-logs', href: `/ssh-activity-logs?user_id=${uid}`, label: 'SSH activity', icon: FileTerminal },
    { route: '/access-requests', href: `/access-requests?q=${encodeURIComponent(user.name || user.email)}`, label: 'Access requests', icon: KeyRound },
  ].filter((l) => canAccessRoute(l.route));

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={usersCrumbs(user.name)}
        icon={<InitialsAvatar name={user.name} email={user.email} src={user.picture_url} />}
        title={user.name}
        badge={<OutcomeBadge shape="chip" value={userStatusValue(user.status)}>{user.status}</OutcomeBadge>}
        description={user.email}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-xl font-medium mb-4 text-[var(--d2d-text-primary)]">Activity</h2>
          <div className="bg-[var(--d2d-bg-card)] shadow rounded-lg p-4 space-y-4">
            <nav aria-label={`${user.name}'s activity`}>
              <ul className="flex flex-wrap gap-2">
                {activityLinks.map(({ href, label, icon: Icon }) => (
                  <li key={href}>
                    <Link
                      to={href}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[var(--d2d-border)] px-3 py-1.5 text-sm text-[var(--d2d-text-primary)] hover:bg-[var(--ds-color-bg-hover)]"
                    >
                      <Icon className="h-4 w-4 text-[var(--d2d-text-muted)]" aria-hidden="true" />
                      {label}
                      <ChevronRight className="h-3.5 w-3.5 text-[var(--d2d-text-muted)]" aria-hidden="true" />
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            {canSeeAccessLogs && (
              <div>
                <h3 className="text-sm font-medium text-[var(--d2d-text-secondary)] mb-2">Latest access decisions</h3>
                {recentLogsQuery.isLoading ? (
                  <Skeleton variant="text" width={240} />
                ) : recentLogsQuery.isError ? (
                  <SectionLoadError what="access logs" error={recentLogsQuery.error} onRetry={() => recentLogsQuery.refetch()} />
                ) : recentLogs.length === 0 ? (
                  <p className="text-sm text-[var(--d2d-text-muted)]">No access decisions recorded for this user.</p>
                ) : (
                  <ul className="divide-y divide-[var(--d2d-border)]">
                    {recentLogs.map((log) => (
                      <li key={log.id}>
                        <Link
                          to={`/access-logs?user_id=${encodeURIComponent(user.id)}&entry=${encodeURIComponent(log.id)}`}
                          className="flex items-center gap-3 py-2 text-sm text-[var(--d2d-text-primary)] hover:bg-[var(--ds-color-bg-hover)]"
                        >
                          {log.result === 'allow'
                            ? <OutcomeBadge value="allow" decision>Allow</OutcomeBadge>
                            : <OutcomeBadge value="deny" decision>Deny</OutcomeBadge>}
                          <span className="min-w-0 flex-1 truncate">{log.action}</span>
                          <Timestamp value={log.created_at} className="text-[var(--d2d-text-muted)]" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium mb-4 text-[var(--d2d-text-primary)]">Assigned Roles</h2>
          <div className="bg-[var(--d2d-bg-card)] shadow rounded-lg p-4">
            {rolesQuery.isError ? (
              <SectionLoadError what="roles" error={rolesQuery.error} onRetry={() => rolesQuery.refetch()} />
            ) : roleBindings.length > 0 ? (
              <ul>
                {roleBindings.map(binding => (
                  <li key={binding.id} className="p-2 text-[var(--d2d-text-primary)]">{binding.role?.name}</li>
                ))}
              </ul>
            ) : (
              <p className="text-[var(--d2d-text-muted)]">No roles assigned.</p>
            )}
          </div>
        </div>

        <div>
          <h2 className="text-xl font-medium mb-4 text-[var(--d2d-text-primary)]">Group Memberships</h2>
          <div className="bg-[var(--d2d-bg-card)] shadow rounded-lg p-4">
            {groupsQuery.isError ? (
              <SectionLoadError what="group memberships" error={groupsQuery.error} onRetry={() => groupsQuery.refetch()} />
            ) : groups.length > 0 ? (
              <ul>
                {groups.map(group => (
                  <li key={group.id} className="p-2 border-b border-[var(--d2d-border)] text-[var(--d2d-text-primary)]">{group.name}</li>
                ))}
              </ul>
            ) : (
              <p className="text-[var(--d2d-text-muted)]">Not a member of any groups.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
